// Construction de la structure de la sidebar (blocs + sous-blocs "Autre")

import { BLOC_ORDER, KNOWN_SET_IDS, KNOWN_SERIE_IDS, SATELLITE_SET_IDS } from "../config/blocs";

// Séries déjà rattachées à un bloc via serieIds : leurs sets non listés
// remontent dans le bloc, jamais dans "Autre".
const SERIE_TO_BLOC = {};
BLOC_ORDER.forEach(bloc => {
  (bloc.serieIds || []).forEach(sid => { SERIE_TO_BLOC[sid] = bloc.key; });
});

export function buildSidebarBlocs(seriesData, prefs) {
  // Les sous-sets satellites (Galerie de Dresseurs/Galaroise) sont fusionnés
  // dans leur set principal côté backend — jamais une entrée de sidebar à
  // part, ni dans un bloc, ni dans "Autre".
  seriesData = seriesData.map(s => ({
    ...s,
    sets: (s.sets || []).filter(set => !SATELLITE_SET_IDS.has(set.id)),
  }));

  // Map set.id → set object (from all series)
  const setById = {};
  const allSets = seriesData.flatMap(s => s.sets || []);
  allSets.forEach(s => { setById[s.id] = s; });

  // Sets d'une série rattachée à un bloc mais pas encore listés dans blocs.js
  // (typiquement : un set qui vient de sortir). Ils vont en TÊTE de leur bloc,
  // les plus récents d'abord — TCGdex renvoie ses sets du plus ancien au plus
  // récent, d'où le reverse.
  const newByBloc = {};
  seriesData.forEach(serie => {
    const blocKey = SERIE_TO_BLOC[serie.id];
    if (!blocKey) return;
    const fresh = (serie.sets || []).filter(s => !KNOWN_SET_IDS.has(s.id));
    if (fresh.length) {
      newByBloc[blocKey] = [...(newByBloc[blocKey] || []), ...fresh].reverse();
    }
  });
  const claimedIds = new Set(Object.values(newByBloc).flat().map(s => s.id));

  // Build main blocs in exact Pokécardex order
  let blocs = BLOC_ORDER.map(bloc => {
    const listed = bloc.setIds
      .map(id => setById[id])
      .filter(Boolean); // skip unknown IDs silently
    return { ...bloc, sets: [...(newByBloc[bloc.key] || []), ...listed] };
  }).filter(b => b.sets.length > 0);

  // "Autre" : une entrée par série non rattachée à un bloc (POP, kits de
  // dresseur, McDonald's, TCG Pocket…), dans l'ordre Pokécardex puis le reste.
  // Chaque série garde son propre nom : autrefois elles finissaient toutes
  // mélangées dans un fourre-tout "Nouveaux sets (non classés)".
  const AUTRE_SERIE_ORDER = ["misc", "pop", "tk", "mc", "tcgp"];
  const unclassified = (sets) =>
    (sets || []).filter(s => !KNOWN_SET_IDS.has(s.id) && !claimedIds.has(s.id));

  const orphanSeries = seriesData.filter(s => !SERIE_TO_BLOC[s.id]);
  orphanSeries.sort((a, b) => {
    const ra = AUTRE_SERIE_ORDER.indexOf(a.id);
    const rb = AUTRE_SERIE_ORDER.indexOf(b.id);
    return (ra < 0 ? AUTRE_SERIE_ORDER.length : ra) - (rb < 0 ? AUTRE_SERIE_ORDER.length : rb);
  });

  const otherSubBlocs = [];
  orphanSeries.forEach(s => {
    const sets = unclassified(s.sets);
    if (sets.length > 0) {
      otherSubBlocs.push({ key: s.id, label: s.name, sets });
    }
  });

  if (otherSubBlocs.length > 0) {
    blocs.push({
      key: "other",
      label: "Autre",
      sets: otherSubBlocs.flatMap(s => s.sets),
      subBlocs: otherSubBlocs,
    });
  }

  return applySidebarPrefs(blocs, prefs);
}

// Applique une préférence de masquage + d'ordre à une liste d'éléments.
// Un élément absent de `order` garde sa position d'origine, à la suite de ceux
// qui ont été explicitement ordonnés : une nouveauté ne disparaît donc jamais
// parce qu'elle n'était pas dans la liste enregistrée.
function arrange(items, keyOf, hiddenKeys, orderKeys) {
  const hidden = new Set(hiddenKeys || []);
  const order = orderKeys || [];
  const rank = new Map(order.map((k, i) => [k, i]));
  return items
    .filter(item => !hidden.has(keyOf(item)))
    .map((item, i) => ({ item, i }))
    .sort((x, y) => {
      const rx = rank.has(keyOf(x.item)) ? rank.get(keyOf(x.item)) : order.length + x.i;
      const ry = rank.has(keyOf(y.item)) ? rank.get(keyOf(y.item)) : order.length + y.i;
      return rx - ry;
    })
    .map(x => x.item);
}

// Préférences utilisateur (panneau Réglages) : masquage et ordre, au niveau des
// blocs ET des sets à l'intérieur de chaque bloc.
export function applySidebarPrefs(blocs, prefs) {
  if (!prefs) return blocs;
  const withSets = blocs.map(bloc => ({
    ...bloc,
    sets: arrange(bloc.sets, s => s.id, prefs.hiddenSets, prefs.setOrder),
    subBlocs: bloc.subBlocs?.map(sub => ({
      ...sub,
      sets: arrange(sub.sets, s => s.id, prefs.hiddenSets, prefs.setOrder),
    })).filter(sub => sub.sets.length > 0),
  }));
  // Un bloc dont tous les sets sont masqués disparaît de lui-même.
  const nonEmpty = withSets.filter(b => b.sets.length > 0);
  return arrange(nonEmpty, b => b.key, prefs.hidden, prefs.order);
}
